// src/web/client/src/components/analytics/RevenueAttribution.jsx
// Revenue Attribution & ROI Tracking Component

import React, { useState, useEffect } from 'react';
import { safeApi, isFeatureError } from '@/utils/api';

// period comes from the page-level selector and is obeyed on every change —
// no internal period state. The old useState(period) captured the prop once
// at mount and fed a second, tab-local selector, so the screen showed two
// period selectors at once and the page-level one silently did nothing here.
export default function RevenueAttribution({ compact = false, period = '30d' }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [messageStats, setMessageStats] = useState({});

  useEffect(() => {
    fetchAttribution();
  }, [period]);

  const fetchAttribution = async () => {
    setLoading(true);
    try {
      const data = await safeApi.get(`/attribution?period=${period}`);
      if (isFeatureError(data)) {
        setError(data);
        return;
      }
      setAttribution(data.data || data.attribution || data);
      // Fetch message stats for ROI table
      try {
        const msgData = await safeApi.get(`/analytics/messages?period=${period}`);
        setMessageStats(msgData.message_performance || {});
      } catch (e) {
        console.log('Message stats fetch skipped');
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch attribution:', err);
      setError({ message: 'Failed to load attribution data' });
    } finally {
      setLoading(false);
    }
  };

  // Feature error
  if (error && error.code === 'FEATURE_NOT_AVAILABLE') {
    return <ProGateMessage />;
  }

  if (loading) {
    return <LoadingState />;
  }

  if (compact) {
    return <CompactView data={attribution} />;
  }
  // Support both old format (attribution.attributed_revenue) and new format (attribution.summary.total_attributed_revenue)
  const summaryData = attribution?.summary || attribution || {};
  // ROI is intentionally disabled by the backend until a real cost model exists
  // (placeholder $0.10/msg basis produced multimillion-% ROI). Single source of
  // truth: the backend's `roi_disabled` flag. When set, hide ROI everywhere
  // (top card, per-type table, compact view) instead of fabricating a number.
  const roiDisabled = summaryData?.roi_disabled === true
    || attribution?.roi_disabled === true
    || summaryData?.roi_percentage === null
    || attribution?.roi_percentage === null;
  const windowHours = attribution?.attribution_window_hours ?? summaryData?.attribution_window_hours ?? 168;
  const windowDays = Math.round(windowHours / 24);
  const summary = {
    total_attributed_revenue: parseFloat(summaryData?.total_attributed_revenue || attribution?.attributed_revenue || 0),
    total_attributed_orders: summaryData?.total_attributed_orders || attribution?.attributed_orders || 0,
    attribution_rate: summaryData?.attribution_rate ?? attribution?.attribution_rate ?? 0,
    roi_percentage: roiDisabled ? null : parseInt(summaryData?.roi_percentage ?? attribution?.roi_percentage ?? 0),
    revenue_per_message: summaryData?.revenue_per_message || (attribution?.messages_sent > 0
      ? (parseFloat(attribution?.attributed_revenue || 0) / attribution.messages_sent).toFixed(2)
      : '0')
  };
  const byType = attribution?.by_message_type || messageStats || {};
  const dailyData = attribution?.daily_attribution || [];
  const topMessages = attribution?.top_performing_messages || [];
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">💰 Revenue Attribution</h2>
            <p className="text-gray-500 mt-1">
              Revenue from customers who purchased within {windowDays} days of receiving an Ecomoria email
            </p>
          </div>

          {/* No tab-local period selector: the page-level one governs the
              whole screen (it also offers 365d, which this one never did). */}
          <button
            onClick={fetchAttribution}
            className="px-4 py-2 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
          >
            Refresh
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* No engaged ("opened or clicked") sublabel here: the headline is a
              pure time-window correlation with no engagement gate, and opens
              are untrusted (Apple MPP — the same reason Open Rate was removed
              from this screen). One definition only — the footnote below. */}
          <StatCard
            label="Attributed Revenue"
            value={`$${(summary.total_attributed_revenue || 0).toLocaleString()}`}
            icon="💰"
            highlight
          />
          {roiDisabled ? (
            <StatCard
              label="Attribution Rate"
              value={`${summary.attribution_rate || 0}%`}
              icon="🎯"
            />
          ) : (
            <StatCard
              label="ROI"
              value={`${summary.roi_percentage || 0}%`}
              icon="📈"
              color={summary.roi_percentage > 0 ? 'green' : 'red'}
            />
          )}
          <StatCard
            label="Attributed Orders"
            value={summary.total_attributed_orders || 0}
            icon="📦"
          />
          <StatCard
            label="Revenue per Message"
            value={`$${summary.revenue_per_message || 0}`}
            icon="✉️"
          />
        </div>

        {/* Honest correlation caveat — this is not a causal claim */}
        <p className="text-xs text-gray-400 mt-4 leading-relaxed">
          These are purchases made within {windowDays} days of an Ecomoria email — a correlation,
          not proof the email caused the purchase. Refunded and cancelled orders are excluded.
        </p>
      </div>

      {/* Per-type breakdown */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {roiDisabled ? '📊 Performance by Message Type' : '📊 ROI by Message Type'}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left border-b">
                <th className="pb-3 font-medium text-gray-500">Type</th>
                <th className="pb-3 font-medium text-gray-500 text-right">Sent</th>
                <th className="pb-3 font-medium text-gray-500 text-right">Click Rate</th>
                <th className="pb-3 font-medium text-gray-500 text-right">Conversions</th>
                <th className="pb-3 font-medium text-gray-500 text-right">Conv. Rate</th>
                <th className="pb-3 font-medium text-gray-500 text-right">Revenue</th>
                {!roiDisabled && (
                  <th className="pb-3 font-medium text-gray-500 text-right">ROI</th>
                )}
              </tr>
            </thead>
             <tbody>
              {Object.entries(byType).map(([type, data]) => {
                const sent = data.sent || data.messages_sent || 0;
                const revenue = data.revenue || data.attributed_revenue || 0;
                // "Conversions" must be capped at sent (one conversion per message at most).
                // Backend now returns either `converted` (count of converted messages from
                // /analytics/messages) or `converted_messages` (from /attribution by_type).
                // Fall back to attributed_orders only if neither is present; clamp to sent.
                const convertedRaw = data.converted_messages
                  ?? data.converted
                  ?? data.attributed_orders
                  ?? 0;
                const converted = Math.min(convertedRaw, sent);
                // Click rate is the reliable engagement signal. Open rate is not
                // shown: Apple Mail Privacy / Gmail proxies auto-open and inflate it.
                const clickRate = Math.min(100, Number(data.click_rate) || 0);
                const convRate = sent > 0
                  ? Math.min(100, Math.round((converted / sent) * 100))
                  : 0;
                // Below this many sends, a rate like "100%" is noise, not signal —
                // flag it (muted + n=) so a 1-of-1 conversion doesn't read as a trend.
                const LOW_SAMPLE = 10;
                const smallSample = sent > 0 && sent < LOW_SAMPLE;
                // ROI per-type is rendered ONLY when the backend has not disabled it.
                // When disabled, the column is hidden entirely (see header above).
                const roi = data.roi ?? null;

                return (
                  <tr key={type} className="border-b last:border-0">
                    <td className="py-3">
                      <span className="font-medium capitalize">
                        {type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-3 text-right">{sent}</td>
                    <td className="py-3 text-right">{clickRate}%</td>
                    <td className="py-3 text-right">{converted}</td>
                    <td className="py-3 text-right">
                      <span
                        className={smallSample ? 'text-gray-400' : ''}
                        title={smallSample ? `Small sample (n=${sent}) — treat with caution` : undefined}
                      >
                        {sent > 0 ? `${convRate}%` : '—'}
                        {smallSample ? <span className="text-gray-400"> (n={sent})</span> : null}
                      </span>
                    </td>
                    <td className="py-3 text-right font-medium">
                      ${revenue.toLocaleString()}
                    </td>
                    {!roiDisabled && (
                      <td className="py-3 text-right">
                        <span className={`font-bold ${roi > 0 ? 'text-green-600' : roi < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {roi == null ? '—' : `${roi > 0 ? '+' : ''}${roi}%`}
                        </span>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Attribution Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📈 Daily Attribution</h3>
        <AttributionChart data={dailyData} />
      </div>

      {/* Top Performing Messages */}
      {topMessages.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🏆 Top Performing Messages</h3>
          <div className="space-y-3">
            {topMessages.slice(0, 5).map((msg, i) => (
              <div 
                key={msg.message_id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold ${
                    i === 0 ? 'bg-yellow-100 text-yellow-700' :
                    i === 1 ? 'bg-gray-200 text-gray-700' :
                    i === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-medium text-gray-900 capitalize">
                      {(msg.message_type || 'unknown').replace(/_/g, ' ')}
                    </p>
                    <p className="text-sm text-gray-500">
                      Sent to {msg.customer_email} • {new Date(msg.sent_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">
                    ${msg.attributed_revenue.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">
                    {msg.attributed_orders} order{msg.attributed_orders !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {attribution?.top_performing_messages?.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <span className="text-2xl">💡</span>
            <div>
              <h4 className="font-medium text-yellow-800">No Attributed Revenue Yet</h4>
              <p className="text-yellow-700 mt-1">
                Revenue is attributed when a customer makes a purchase within {windowDays} days
                of receiving a message. Keep sending messages to see attribution data.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function CompactView({ data }) {
  const summary = data?.summary || {};
  const roiDisabled = summary?.roi_disabled === true || summary?.roi_percentage == null;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">💰 Revenue Attribution</h3>
        {roiDisabled ? (
          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
            Attribution: {summary.attribution_rate || 0}%
          </span>
        ) : (
          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
            ROI: {summary.roi_percentage || 0}%
          </span>
        )}
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Attributed Revenue</span>
          <span className="font-bold text-green-600">
            ${(summary.total_attributed_revenue || 0).toLocaleString()}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Attributed Orders</span>
          <span className="font-medium">{summary.total_attributed_orders || 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Attribution Rate</span>
          <span className="font-medium">{summary.attribution_rate || 0}%</span>
        </div>
      </div>
      
      <div className="mt-4 pt-4 border-t">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Revenue per Message</span>
          <span className="font-medium">${summary.revenue_per_message || 0}</span>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, highlight = false, color = 'gray', sublabel = null }) {
  const colorClasses = {
    gray: 'text-gray-900',
    green: 'text-green-600',
    red: 'text-red-600'
  };

  return (
    <div className={`rounded-lg p-4 ${highlight ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{icon}</span>
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${colorClasses[color]}`}>{value}</p>
      {sublabel && (
        <p className="text-xs text-gray-500 mt-1">{sublabel}</p>
      )}
    </div>
  );
}

function AttributionChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-40 flex items-center justify-center text-gray-400">
        No data available
      </div>
    );
  }

  const maxRevenue = Math.max(...data.map(d => d.attributed_revenue), 1);
  const maxMessages = Math.max(...data.map(d => d.messages_sent), 1);

  // Show last 14 days max
  const displayData = data.slice(-14);

  return (
    <div>
      <div className="flex items-end justify-between gap-1 h-40">
        {displayData.map((day, i) => (
          <div key={day.date} className="flex-1 flex flex-col items-center relative group">
            {/* Revenue bar */}
            <div 
              className="w-full bg-green-500 rounded-t opacity-80 transition-all"
              style={{ 
                height: `${(day.attributed_revenue / maxRevenue) * 100}%`, 
                minHeight: day.attributed_revenue > 0 ? '8px' : '2px' 
              }}
            />
            
            {/* Tooltip */}
            <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
              <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                <p>${day.attributed_revenue.toLocaleString()}</p>
                <p className="text-gray-400">{day.messages_sent} messages</p>
              </div>
            </div>
            
            {/* Date label */}
            <span className="text-xs text-gray-400 mt-2 transform -rotate-45 origin-center">
              {new Date(day.date).getDate()}
            </span>
          </div>
        ))}
      </div>
      
      <div className="flex items-center justify-center gap-4 mt-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded" />
          <span className="text-gray-500">Attributed Revenue</span>
        </div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-gray-200 rounded w-1/3"></div>
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-20 bg-gray-200 rounded"></div>
          ))}
        </div>
        <div className="h-40 bg-gray-200 rounded"></div>
      </div>
    </div>
  );
}

function ProGateMessage() {
  return (
    <div className="bg-white rounded-lg shadow p-8 text-center">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="text-3xl">💰</span>
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">Revenue Attribution</h3>
      <p className="text-gray-600 mb-6">
        See how much revenue comes from customers who purchased after receiving your emails.
      </p>
      <p className="text-sm text-purple-600">
        Available on Basic and Pro plans
      </p>
    </div>
  );
}